import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { CaretDown, Circle, Files, MagnifyingGlass, SortDescending, User, X } from 'phosphor-react';

import { ICriadorDeContratos } from '../../../../hooks/useSocietario';

import {
  AbaDaListagem,
  OPCOES_DE_ORDEM,
  motivoSemStatus,
  OrdemDaListagem,
  ORDEM_PADRAO,
  opcoesDeStatusDaAba,
  STATUS_RASCUNHOS,
} from '../listagem';

// Espera o usuario parar de digitar antes de buscar (uma consulta por pausa).
export const ESPERA_DA_BUSCA_MS = 350;

const OPCOES_DE_TIPO = [
  { value: 'constituicao', label: 'Constituição', cor: '#3B82F6' },
  { value: 'alteracao', label: 'Alteração', cor: '#7C3AED' },
];

// Mesmas cores dos selos de situação nos cards.
const COR_DO_STATUS: Record<string, string> = {
  [STATUS_RASCUNHOS]: '#94A3B8',
  em_revisao: '#F59E0B',
  gerado: '#1B88F5',
};

// Busca de nome sem acento e sem caixa ("jose" acha "José").
const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// No botão cabe pouco (no celular, "Última atualização" cortava): o menu mostra o
// rótulo inteiro, e o ícone de ordenar diz o que o botão faz.
const ORDEM_NO_BOTAO: Record<OrdemDaListagem, string> = {
  atualizados: 'Atualização',
  recentes: 'Mais novos',
  antigos: 'Mais antigos',
};

// Grupo segmentado como o dos filtros da Legalização: bloco branco com borda (a mesma
// linguagem da busca) e o segmento ATIVO tingido — o botão diz o que está filtrando.
const segmentoFiltro = (ativo: boolean) => ({
  size: 'sm' as const,
  variant: 'ghost' as const,
  // Sem raio próprio: quem arredonda é o contêiner, que recorta as pontas.
  borderRadius: 0,
  h: '32px',
  px: 3,
  flexShrink: 0,
  fontSize: '13px',
  fontWeight: '700',
  color: ativo ? 'blue.600' : 'gray.600',
  bg: ativo ? 'blue.50' : 'transparent',
  _hover: { bg: ativo ? 'blue.100' : 'blackAlpha.50' },
  _active: { bg: ativo ? 'blue.100' : 'blackAlpha.100' },
});

const DivisorSegmento = () => <Box w="1px" my="6px" bg="gray.200" flexShrink={0} />;

/** Linha do menu: ponto colorido à esquerda e, se houver, a contagem à direita. */
const LinhaFiltro: React.FC<{ cor?: string; rotulo: string; n?: number }> = ({
  cor,
  rotulo,
  n,
}) => (
  <Flex align="center" justify="space-between" w="full" gap={4}>
    <Flex align="center" gap={2} minW={0}>
      <Box w="7px" h="7px" borderRadius="full" bg={cor ?? 'transparent'} flexShrink={0} />
      <Text as="span" noOfLines={1}>
        {rotulo}
      </Text>
    </Flex>
    {n !== undefined && (
      <Text
        as="span"
        fontSize="11px"
        color="gray.500"
        fontWeight="700"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {n}
      </Text>
    )}
  </Flex>
);

// Os menus vão num Portal com zIndex: os cards (GlassCard) têm camadas próprias e,
// sem isto, o menu aberto sobre a grade ficava por baixo deles (visto no menu "...").
const estiloDoMenu = {
  py: 1,
  borderColor: 'gray.200',
  boxShadow: '0 8px 28px rgba(15,23,42,.14)',
  fontSize: '13px',
  zIndex: 'popover',
};

interface FiltrosDaListagemProps {
  aba: AbaDaListagem;
  /** Busca aplicada (a da URL). */
  busca: string;
  onBusca: (valor: string) => void;
  tipo: string;
  onTipo: (valor: string) => void;
  status: string;
  onStatus: (valor: string) => void;
  ordem: OrdemDaListagem;
  onOrdem: (valor: OrdemDaListagem) => void;
  /** Id de quem criou (texto da URL), ou vazio. */
  criadoPor: string;
  onCriadoPor: (valor: string) => void;
  /** Quem abriu contratos ativos, com quantos (as opções do filtro). */
  criadores: ICriadorDeContratos[];
}

// Busca + filtros SEMPRE visíveis (pedido do user, 28/09: sem o botão "Filtros").
// Menu do Chakra em vez de <select>: a lista aberta do select nativo é desenhada pelo
// sistema operacional e não aceita estilo (a Legalização trocou pelo mesmo motivo).
export const FiltrosDaListagem: React.FC<FiltrosDaListagemProps> = ({
  aba,
  busca,
  onBusca,
  tipo,
  onTipo,
  status,
  onStatus,
  ordem,
  onOrdem,
  criadoPor,
  onCriadoPor,
  criadores,
}) => {
  const [texto, setTexto] = useState(busca);
  const [buscaCriador, setBuscaCriador] = useState('');
  // O que ESTA barra mandou por ultimo: separa o eco da propria busca de uma troca
  // vinda de fora (Limpar filtros, voltar do navegador), que reescreve o campo.
  const enviado = useRef(busca);
  const onBuscaAtual = useRef(onBusca);
  onBuscaAtual.current = onBusca;

  useEffect(() => {
    if (busca !== enviado.current) {
      enviado.current = busca;
      setTexto(busca);
    }
  }, [busca]);

  const aplicar = (valor: string) => {
    enviado.current = valor;
    onBuscaAtual.current(valor);
  };

  useEffect(() => {
    const limpo = texto.trim();
    if (limpo === enviado.current) return undefined;
    // O Enter pode ter aplicado antes do prazo: sem a checagem, a mesma busca ia de
    // novo para a URL (entrada duplicada no histórico).
    const espera = setTimeout(() => {
      if (limpo !== enviado.current) aplicar(limpo);
    }, ESPERA_DA_BUSCA_MS);
    return () => clearTimeout(espera);
  }, [texto]);

  const opcoesDeStatus = opcoesDeStatusDaAba(aba);
  const tipoEscolhido = OPCOES_DE_TIPO.find(o => o.value === tipo);
  const statusEscolhido = opcoesDeStatus.find(o => o.value === status);
  const semStatus = opcoesDeStatus.length === 0;
  // Na aba Minhas o filtro já é "criado por mim": o segmento fica desligado.
  const naAbaMinhas = aba === 'minhas';
  // A lista de quem criou segue o tipo e a busca: a pessoa escolhida pode sumir dela (sem
  // contrato na busca atual). O nome fica guardado para o botão não virar "Usuário #7", e
  // ela continua no menu (com 0), marcada.
  const nomesConhecidos = useRef(new Map<number, string>());
  criadores.forEach(c => nomesConhecidos.current.set(c.id, c.nome));
  const idEscolhido = criadoPor ? Number(criadoPor) : null;
  const escolhidoNaLista = criadores.find(c => c.id === idEscolhido);
  const criadorEscolhido =
    escolhidoNaLista ??
    (idEscolhido != null
      ? {
          id: idEscolhido,
          nome: nomesConhecidos.current.get(idEscolhido) ?? `Usuário #${idEscolhido}`,
          total: 0,
        }
      : undefined);
  const opcoesDeCriadores =
    escolhidoNaLista || !criadorEscolhido ? criadores : [criadorEscolhido, ...criadores];
  const termoDoCriador = normalizar(buscaCriador.trim());
  const criadoresVisiveis = termoDoCriador
    ? opcoesDeCriadores.filter(c => normalizar(c.nome).includes(termoDoCriador))
    : opcoesDeCriadores;

  return (
    <>
      <InputGroup size="sm" flex="1 1 260px" maxW={{ base: '100%', lg: '380px' }}>
        <InputLeftElement pointerEvents="none">
          <MagnifyingGlass size={15} color="#94A3B8" />
        </InputLeftElement>
        <Input
          aria-label="Buscar contratos"
          placeholder="Buscar por empresa, CNPJ, nº ou sócio"
          // O back recusa busca com mais de 120 caracteres.
          maxLength={120}
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') aplicar(texto.trim());
            if (e.key === 'Escape' && texto) {
              setTexto('');
              aplicar('');
            }
          }}
          borderRadius="full"
          fontSize="13px"
          bg="white"
          borderColor="gray.200"
          boxShadow="0 1px 2px rgba(15,23,42,.04)"
          _hover={{ borderColor: 'gray.300' }}
          _focusVisible={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182CE' }}
        />
        {texto && (
          <InputRightElement>
            <IconButton
              aria-label="Limpar busca"
              icon={<X size={12} weight="bold" />}
              size="xs"
              h="20px"
              minW="20px"
              borderRadius="full"
              variant="ghost"
              onClick={() => {
                setTexto('');
                aplicar('');
              }}
            />
          </InputRightElement>
        )}
      </InputGroup>

      <Flex
        role="group"
        aria-label="Filtros"
        bg="white"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="full"
        boxShadow="0 1px 2px rgba(15,23,42,.04)"
        // Rola para o lado em tela muito estreita, em vez de espremer os segmentos.
        overflowX="auto"
        sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
        flexShrink={0}
        maxW="100%"
      >
        <Menu placement="bottom-start">
          <MenuButton
            as={Button}
            aria-label={`Tipo: ${tipoEscolhido?.label ?? 'todos'}`}
            {...segmentoFiltro(!!tipoEscolhido)}
            leftIcon={<Files size={14} weight={tipoEscolhido ? 'fill' : 'bold'} />}
            rightIcon={<CaretDown size={11} weight="bold" />}
          >
            {tipoEscolhido?.label ?? 'Tipo'}
          </MenuButton>
          <Portal>
            <MenuList minW="200px" {...estiloDoMenu}>
              <MenuOptionGroup type="radio" value={tipo} onChange={v => onTipo(v as string)}>
                <MenuItemOption value="">
                  <LinhaFiltro rotulo="Todos os tipos" />
                </MenuItemOption>
                {OPCOES_DE_TIPO.map(o => (
                  <MenuItemOption key={o.value} value={o.value}>
                    <LinhaFiltro cor={o.cor} rotulo={o.label} />
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Portal>
        </Menu>

        <DivisorSegmento />

        <Menu placement="bottom-start">
          <Tooltip
            label={motivoSemStatus[aba] ?? ''}
            isDisabled={!semStatus}
            placement="top"
            fontSize="xs"
          >
            {/* span: botão desabilitado não dispara o mouse, e o tooltip não abriria. */}
            <Box as="span" display="inline-flex">
              <MenuButton
                as={Button}
                aria-label={`Status: ${statusEscolhido?.label ?? 'todos'}`}
                {...segmentoFiltro(!!statusEscolhido)}
                isDisabled={semStatus}
                leftIcon={<Circle size={14} weight={statusEscolhido ? 'fill' : 'bold'} />}
                rightIcon={<CaretDown size={11} weight="bold" />}
              >
                {statusEscolhido?.label ?? 'Status'}
              </MenuButton>
            </Box>
          </Tooltip>
          <Portal>
            <MenuList minW="210px" {...estiloDoMenu}>
              <MenuOptionGroup type="radio" value={status} onChange={v => onStatus(v as string)}>
                <MenuItemOption value="">
                  <LinhaFiltro rotulo="Todos os status" />
                </MenuItemOption>
                {opcoesDeStatus.map(o => (
                  <MenuItemOption key={o.value} value={o.value}>
                    <LinhaFiltro cor={COR_DO_STATUS[o.value]} rotulo={o.label} />
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Portal>
        </Menu>

        <DivisorSegmento />

        {/* Quem criou (29/09): como o "Responsável" do kanban da Legalização — lista de quem
            tem contrato, com busca pelo nome e quantos cada um tem. */}
        <Menu placement="bottom-start" onClose={() => setBuscaCriador('')}>
          <Tooltip
            label="Na aba Minhas solicitações todos já são seus"
            isDisabled={!naAbaMinhas}
            placement="top"
            fontSize="xs"
          >
            <Box as="span" display="inline-flex">
              <MenuButton
                as={Button}
                aria-label={`Criado por: ${criadorEscolhido?.nome ?? 'todos'}`}
                {...segmentoFiltro(!!criadorEscolhido && !naAbaMinhas)}
                isDisabled={naAbaMinhas}
                maxW="200px"
                leftIcon={<User size={14} weight={criadorEscolhido ? 'fill' : 'bold'} />}
                rightIcon={<CaretDown size={11} weight="bold" />}
                sx={{ '& > span': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
              >
                {(!naAbaMinhas && criadorEscolhido?.nome) || 'Criado por'}
              </MenuButton>
            </Box>
          </Tooltip>
          <Portal>
            <MenuList minW="252px" {...estiloDoMenu}>
              <Box px={2} pb={2} pt={1}>
                <Input
                  size="sm"
                  fontSize="13px"
                  borderRadius="md"
                  aria-label="Buscar quem criou"
                  placeholder="Buscar pelo nome..."
                  value={buscaCriador}
                  onChange={e => setBuscaCriador(e.target.value)}
                  // O Menu do Chakra navega pelos itens com as teclas; sem isto, digitar
                  // aqui pularia o foco para a lista.
                  onKeyDown={e => e.stopPropagation()}
                />
              </Box>
              <Box maxH="300px" overflowY="auto">
                <MenuOptionGroup
                  type="radio"
                  value={criadoPor}
                  onChange={v => onCriadoPor(v as string)}
                >
                  <MenuItemOption value="">
                    <LinhaFiltro rotulo="Todos" />
                  </MenuItemOption>
                  {criadoresVisiveis.map(c => (
                    <MenuItemOption key={c.id} value={String(c.id)}>
                      <LinhaFiltro cor="#1B88F5" rotulo={c.nome} n={c.total} />
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
                {criadoresVisiveis.length === 0 && (
                  <Text px={3} py={2} fontSize="11px" color="gray.400">
                    {termoDoCriador
                      ? 'Nenhum nome com esse termo'
                      : 'Ninguém criou contratos ainda'}
                  </Text>
                )}
              </Box>
            </MenuList>
          </Portal>
        </Menu>

        <DivisorSegmento />

        {/* Ordem: não é filtro, mas mora no mesmo grupo; só tinge fora do padrão. */}
        <Menu placement="bottom-end">
          <MenuButton
            as={Button}
            aria-label={`Ordenar: ${ORDEM_NO_BOTAO[ordem]}`}
            {...segmentoFiltro(ordem !== ORDEM_PADRAO)}
            leftIcon={<SortDescending size={14} weight="bold" />}
            rightIcon={<CaretDown size={11} weight="bold" />}
          >
            {ORDEM_NO_BOTAO[ordem]}
          </MenuButton>
          <Portal>
            <MenuList minW="230px" {...estiloDoMenu}>
              <MenuOptionGroup
                type="radio"
                title="Ordenar por"
                value={ordem}
                onChange={v => onOrdem(v as OrdemDaListagem)}
              >
                {OPCOES_DE_ORDEM.map(o => (
                  <MenuItemOption key={o.value} value={o.value}>
                    <LinhaFiltro rotulo={o.label} />
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Portal>
        </Menu>
      </Flex>
    </>
  );
};

export default FiltrosDaListagem;
